<script lang="ts">
  /**
   * PUBLIC webhook documentation — `/webhooks`. No session, no token.
   *
   * It is public because the person who needs it is usually NOT our customer: it is the
   * developer their customer handed the job to, and making them sign in to read a field
   * table helps nobody.
   *
   * The field tables come from `lib/webhookSchema`, which a backend test holds to the
   * real payload builder. Nothing on this page is typed out by hand twice.
   */
  import { FIELD_GROUPS, INTAKE, EVENTS, NOT_AVAILABLE, LIFECYCLE } from '../lib/webhookSchema';

  const SAMPLE = {
    event: 'member.role_changed',
    data: {
      community_id: 42,
      community_slug: 'example-community',
      community_name: 'Example Community',
      member_id: 1234,
      member_external_id: 'b7f3c1d9e2a44f6b8c0d1e2f3a4b5c6d',
      name: 'Jordan Rivera',
      first_name: 'Jordan',
      last_name: 'Rivera',
      handle: 'jordan-rivera-1234',
      profile_url: 'https://www.skool.com/@jordan-rivera-1234',
      avatar_url: 'https://assets.skool.com/avatars/example.jpg',
      bio: 'Building things on the internet.',
      location: 'Lisbon, Portugal',
      time_zone: 'Europe/Lisbon',
      email: 'jordan@example.com',
      contact_email: null,
      billing_email: null,
      survey_email: 'jordan@example.com',
      phone: null,
      status: 'active',
      mrr_status: 'hardcore',
      skool_role: 'member',
      joined_at: '2026-03-14T09:20:00+00:00',
      cancelled_at: null,
      declined_at: null,
      ends_at: null,
      departed_at: null,
      plan_tier: 'premium',
      plan_amount_cents: 4900,
      plan_currency: 'USD',
      plan_interval: 'month',
      ltv_cents: 29400,
      first_paid_at: '2026-03-14T09:25:00+00:00',
      invited_by: null,
      attribution: 'invite',
      posts: 12,
      comments: 47,
      activity: 71,
      upvotes_received: 103,
      replies_earned: 38,
      momentum: 0.42,
      stage: 'steady',
      first_active_at: '2026-03-15T11:02:00+00:00',
      last_active_at: '2026-09-17T16:45:00+00:00',
      contributor_role: 'key_voice',
      contributor_role_previous: 'spectator',
      contributor_role_changed_at: '2026-09-18T08:30:00+00:00',
      intake_count: 2,
      intake_1_question: 'What is your best email address?',
      intake_1_type: 'email',
      intake_1_answer: 'jordan@example.com',
      intake_2_question: 'What are you hoping to get out of this?',
      intake_2_type: 'text',
      intake_2_answer: 'Ship my first product.',
    },
  };

  const VERIFY = `# Python — verify before you parse
import hashlib, hmac, time

def verify(raw_body: bytes, header: str, secret: str) -> bool:
    parts = dict(p.split("=", 1) for p in header.split(","))
    t, got = parts["t"], parts["v1"]
    if abs(time.time() - int(t)) > 300:          # older than 5 minutes
        return False
    want = hmac.new(secret.encode(), f"{t}.".encode() + raw_body,
                    hashlib.sha256).hexdigest()
    return hmac.compare_digest(want, got)        # constant time`;
</script>

<svelte:head>
  <title>Webhooks — Sideguide</title>
</svelte:head>

<div class="min-h-screen bg-base-200">
  <div class="max-w-4xl mx-auto px-4 py-10 flex flex-col gap-10">

    <header class="flex flex-col gap-2">
      <p class="text-xs uppercase tracking-wide text-base-content/50">Sideguide</p>
      <h1 class="text-3xl font-bold">Webhooks</h1>
      <p class="text-base-content/70 max-w-2xl">
        We POST your members to a URL you choose, the moment something changes. This page
        is the whole contract: what arrives, what every field means, and how to prove it
        came from us.
      </p>
    </header>

    <!-- ===================== the envelope ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">What arrives</h2>
      <p class="text-sm text-base-content/70">
        A <code>POST</code> with a JSON body. Two top-level keys — <code>event</code> and
        <code>data</code>. <strong>Read your fields off <code>data</code>, not off the
        root</strong>; that is the single most common mistake.
      </p>
      <p class="text-sm text-base-content/70">
        <code>data</code> is <strong>flat</strong>: no nesting, every key always present,
        and any value may be <code>null</code>. A key never disappears just because it has
        no value, so you can map fields once and trust the mapping.
      </p>
      <div class="overflow-x-auto">
        <!-- privacy-ok: a synthetic example ("Jordan Rivera"), never a real member -->
        <pre class="text-xs bg-base-100 rounded-lg p-4 ring-1 ring-base-content/10 max-h-96 overflow-y-auto"><code
          >{JSON.stringify(SAMPLE, null, 2)}</code></pre>
      </div>
    </section>

    <!-- ===================== events ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">Events</h2>
      <p class="text-sm text-base-content/70">Three, today.</p>
      <div class="overflow-x-auto">
        <table class="table table-sm bg-base-100 rounded-lg">
          <thead>
            <tr><th>event</th><th>Fires when</th><th>Repeats?</th></tr>
          </thead>
          <tbody>
            {#each EVENTS as e (e.name)}
              <tr>
                <!-- privacy-ok: e.name is an EVENT id ("member.joined"), not a person -->
                <td class="font-mono text-xs whitespace-nowrap align-top">{e.name}</td>
                <td class="text-sm align-top">{e.when}</td>
                <td class="text-sm align-top whitespace-nowrap">{e.repeats}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div class="alert alert-warning text-sm flex-col items-start gap-2">
        <span><strong>Still not covered.</strong> Nothing is delivered when any of these
          happen:</span>
        <ul class="list-disc ml-5">
          {#each NOT_AVAILABLE as item}<li>{item}</li>{/each}
        </ul>
        <span>
          <code>member.role_changed</code> is about <em>contribution</em> — how much someone
          posts and how well it lands — not about money. For trial conversions use
          <code>member.converted_to_paid</code> and <code>member.trial_payment_failed</code>
          above; they are the two that carry billing meaning today.
        </span>
      </div>
    </section>

    <!-- ===================== lifecycle semantics ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">What the values mean</h2>
      <p class="text-sm text-base-content/70">
        How to recognise each real-world state from the fields you receive.
      </p>
      <div class="flex flex-col gap-3">
        {#each LIFECYCLE as row (row.scenario)}
          <div class="bg-base-100 rounded-lg p-4 ring-1
                      {row.supported ? 'ring-base-content/10' : 'ring-warning/50'}">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="font-semibold text-sm">{row.scenario}</h3>
              {#if !row.supported}
                <span class="badge badge-warning badge-sm">not available</span>
              {/if}
            </div>
            <!-- privacy-ok: static documentation prose from lib/webhookSchema, no member data -->
            <p class="text-sm mt-1">{row.answer}</p>
            <p class="text-xs text-base-content/60 mt-1">{row.fields}</p>
          </div>
        {/each}
      </div>

      <div class="alert alert-error text-sm">
        <span>
          <strong><code>mrr_status</code> is not a billing field.</strong> The name is
          Skool’s, not ours, and it is misleading: the values are activity badges
          (<code>hardcore</code>, <code>clover</code>, <code>liftoff</code>,
          <code>crown</code>, <code>diamond</code>, <code>fire</code>), and it is
          <code>null</code> for most members because it only exists for people we have seen
          post. Never branch on it for money. Use <code>status</code>,
          <code>plan_amount_cents</code>, <code>cancelled_at</code>,
          <code>declined_at</code> and <code>ends_at</code>.
        </span>
      </div>
    </section>

    <!-- ===================== fields ===================== -->
    <section class="flex flex-col gap-4">
      <h2 class="text-xl font-semibold">Every field</h2>
      {#each FIELD_GROUPS as group (group.title)}
        <div class="flex flex-col gap-2">
          <!-- privacy-ok: a section heading ("Community", "What they pay"), not content -->
          <h3 class="font-semibold text-sm">{group.title}</h3>
          <p class="text-xs text-base-content/60">{group.blurb}</p>
          <div class="overflow-x-auto">
            <table class="table table-xs bg-base-100 rounded-lg">
              <tbody>
                {#each group.fields as f (f.name)}
                  <!-- privacy-ok: f.name is a FIELD name ("plan_amount_cents") and f.note is
                       our own description of it — this page renders no member data at all -->
                  <tr>
                    <td class="font-mono text-xs whitespace-nowrap align-top">{f.name}</td>
                    <td class="text-xs text-base-content/50 align-top">{f.type}</td>
                    <td class="text-xs align-top">{f.note}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {/each}

      <div class="flex flex-col gap-2">
        <h3 class="font-semibold text-sm">…then the answers, repeated</h3>
        <p class="text-xs text-base-content/60">
          Replace <code>N</code> with 1, 2, 3 … up to <code>intake_count</code>.
        </p>
        <div class="overflow-x-auto">
          <table class="table table-xs bg-base-100 rounded-lg">
            <tbody>
              {#each INTAKE as f (f.name)}
                <!-- privacy-ok: f.name is a FIELD name pattern ("intake_N_answer") and
                     f.note our description of it — no member data on this page -->
                <tr>
                  <td class="font-mono text-xs whitespace-nowrap align-top">{f.name}</td>
                  <td class="text-xs text-base-content/50 align-top">{f.type}</td>
                  <td class="text-xs align-top">{f.note}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ===================== signature ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">Proving it came from us</h2>
      <p class="text-sm text-base-content/70">
        Every delivery carries <code>Sideguide-Signature</code>, shaped
        <code>t=&lt;unix seconds&gt;,v1=&lt;hex&gt;</code>. <code>v1</code> is
        HMAC-SHA256 over <code>"&lt;t&gt;." + the raw request body</code>, keyed with the
        signing secret shown once when the webhook was created. The timestamp is inside the
        signed material, so a captured delivery cannot be replayed later.
      </p>
      <div class="overflow-x-auto">
        <pre class="text-xs bg-base-100 rounded-lg p-4 ring-1 ring-base-content/10"><code
          >{VERIFY}</code></pre>
      </div>
      <p class="text-sm text-base-content/70">
        Verify against the <strong>raw bytes</strong>, before any JSON parsing or
        re-serialising — a framework that re-encodes the body will break the comparison.
      </p>
    </section>

    <!-- ===================== delivery rules ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">Delivery</h2>
      <ul class="list-disc ml-5 text-sm text-base-content/70 flex flex-col gap-1">
        <li>Answer <strong>2xx</strong> as fast as you can. Do the work afterwards.</li>
        <li>Anything else is retried <strong>up to 3 times</strong>, five minutes apart,
          then parked and left for a person.</li>
        <li><strong>Be idempotent.</strong> The same delivery can arrive more than once —
          dedupe on the delivery id below.</li>
        <li>HTTPS only. We will not call a private, loopback or link-local address, and we
          do not follow redirects.</li>
        <li>We read only the status code. Your response body is discarded.</li>
      </ul>
    </section>

    <!-- ===================== dedupe ===================== -->
    <section class="flex flex-col gap-3">
      <h2 class="text-xl font-semibold">Recognising a repeat</h2>
      <p class="text-sm text-base-content/70">
        We deliver <strong>at least once</strong>. A retry, or a delivery we sent but
        never managed to record, arrives again with <strong>byte-identical body</strong> —
        it is not rebuilt from whatever the member looks like now, so two copies of the
        same delivery always say the same thing.
      </p>
      <p class="text-sm text-base-content/70">
        Every request carries <code>Sideguide-Delivery</code>, shaped
        <code>&lt;delivery&gt;.&lt;attempt&gt;</code> — for example
        <code>918.2</code>, the second try at delivery 918.
      </p>
      <ul class="list-disc ml-5 text-sm text-base-content/70 flex flex-col gap-1">
        <li><strong>Dedupe on the part before the dot.</strong> That is the delivery, and
          it is the same for every attempt at it. The attempt number is there for your
          logs; it may repeat if we failed to record an attempt, so it is not a key.</li>
        <li>Store the delivery id when you finish processing, and drop anything whose id
          you already hold.</li>
        <li>This header is <strong>not signed</strong>, and it does not need to be — the
          signature already proves the request came from us. Verify that first, then
          dedupe.</li>
      </ul>
      <p class="text-sm text-base-content/70">
        A manual <em>Send test</em> from the dashboard carries no delivery id: it belongs
        to no delivery and is never retried.
      </p>
    </section>

    <footer class="text-xs text-base-content/40 pt-4 border-t border-base-content/10">
      Set webhooks up in Sideguide under <strong>Automations → Webhooks</strong>.
    </footer>
  </div>
</div>
